import React, { useState, useRef, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { Mic, Square, X, Loader2, RotateCcw, Play, Pause, Check, AlertTriangle, Keyboard, ExternalLink, Share2, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import Modal from "@/components/Modal";
import { Btn, Input, Select, Textarea, Field, Badge } from "@/components/common";
import { api, apiError } from "@/lib/api";

const ETYPES = ["person", "business", "organization", "place", "group", "event"];
const REL_TYPES = ["knows", "spouse", "partner", "family", "friend", "colleague", "introduced_by", "works_for", "owns", "member_of", "neighbor_of", "supplies", "collaborates_with", "participated_same_event", "other"];
const MAX_SECONDS = 90;
const newRef = () => Math.random().toString(36).slice(2, 10);

function cardToDraft(d, originId, sourceEntity) {
  const c = d.card || {};
  const matchedOrigin = d.origin_match?.id || originId || "";
  const reminder = d.reminder_date || "";
  return {
    pRef: newRef(), bRef: newRef(),
    person_name: c.person_name || "", business: c.business || "", role: c.role || "",
    phones: (c.phones || []).join(", "), emails: (c.emails || []).join(", "),
    address: c.address || "", website: c.website || "", notes: c.notes || "",
    person_type: "person", business_type: "business",
    relationship_type: c.relationship_type || (c.business ? "works_for" : "knows"),
    phone_ambiguous: !!c.phone_ambiguous, phone_owner: "person",
    uncertain_fields: c.uncertain_fields || [],
    add_membership: !!matchedOrigin, membership_origin: matchedOrigin,
    origin_auto: d.origin_match?.name || "",
    person_action: "create", person_link_id: "", context_target: "source",
    business_action: "create", business_link_id: "",
    person_dups: d.person_duplicates || [], business_dups: d.business_duplicates || [],
    context_connect: !!sourceEntity, context_rel_type: "knows",
    make_task: !!(c.follow_up || reminder), task_title: c.follow_up || (reminder ? "Follow up" : ""),
    reminder_date: reminder, reminder_auto: !!reminder,
    related: (d.related || []).map((r) => ({
      relRef: newRef(), name: r.name || "", relationship_type: r.relationship_type || "knows",
      phrase: r.phrase || "", dups: r.duplicates || [],
      include: true,
      // When candidates exist, force an explicit choice — never pre-select a person.
      action: (r.duplicates && r.duplicates.length) ? "" : "create",
      link_id: "",
    })),
  };
}

export default function VoiceContactModal({ workspaceId, originId, sourceEntity, onClose, onSaved }) {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("idle"); // idle | recording | processing | review | saved | error
  const [seconds, setSeconds] = useState(0);
  const [transcript, setTranscript] = useState("");
  const [drafts, setDrafts] = useState([]);
  const [error, setError] = useState("");
  const [audioUrl, setAudioUrl] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [origins, setOrigins] = useState([]);
  const [status, setStatus] = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedResult, setSavedResult] = useState(null);
  const [liveFinal, setLiveFinal] = useState("");
  const [liveInterim, setLiveInterim] = useState("");
  const liveSupported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);
  const recognitionRef = useRef(null);

  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const streamRef = useRef(null);
  const timerRef = useRef(null);
  const blobRef = useRef(null);
  const audioElRef = useRef(null);
  const idemRef = useRef(newRef() + newRef());

  useEffect(() => {
    api.get(`/origins?workspace_id=${workspaceId}&limit=100`).then((r) => setOrigins(r.data.items)).catch(() => {});
    api.get(`/voice/status`).then((r) => setStatus(r.data)).catch(() => {});
    return () => stopEverything();
    // eslint-disable-next-line
  }, [workspaceId]);

  const stopEverything = useCallback(() => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { if (mediaRef.current && mediaRef.current.state !== "inactive") mediaRef.current.stop(); } catch {}
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    try { if (recognitionRef.current) { recognitionRef.current.onend = null; recognitionRef.current.stop(); recognitionRef.current = null; } } catch {}
  }, []);

  const startLiveCaptions = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) return;
    try {
      const rec = new SR();
      rec.continuous = true; rec.interimResults = true; rec.lang = "en-US";
      rec.onresult = (ev) => {
        let fin = "", interim = "";
        for (let i = ev.resultIndex; i < ev.results.length; i++) {
          const r = ev.results[i];
          if (r.isFinal) fin += r[0].transcript; else interim += r[0].transcript;
        }
        if (fin) setLiveFinal((p) => (p + " " + fin).trim());
        setLiveInterim(interim);
      };
      rec.onerror = () => {};
      rec.onend = () => { if (recognitionRef.current === rec && mediaRef.current && mediaRef.current.state === "recording") { try { rec.start(); } catch {} } };
      recognitionRef.current = rec;
      rec.start();
    } catch {}
  };

  const startRecording = async () => {
    setError("");
    if (!navigator.mediaDevices?.getUserMedia || typeof MediaRecorder === "undefined") {
      setError("This browser does not support audio recording. Please type the contact instead.");
      setPhase("error");
      return;
    }
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      let mime = "";
      for (const m of ["audio/webm;codecs=opus", "audio/webm", "audio/mp4"]) {
        if (MediaRecorder.isTypeSupported(m)) { mime = m; break; }
      }
      const mr = mime ? new MediaRecorder(stream, { mimeType: mime }) : new MediaRecorder(stream);
      mediaRef.current = mr;
      chunksRef.current = [];
      mr.ondataavailable = (e) => { if (e.data && e.data.size > 0) chunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: mr.mimeType || "audio/webm" });
        blobRef.current = blob;
        if (audioUrl) URL.revokeObjectURL(audioUrl);
        setAudioUrl(URL.createObjectURL(blob));
      };
      mr.start();
      setSeconds(0);
      setLiveFinal(""); setLiveInterim("");
      startLiveCaptions();
      setPhase("recording");
      timerRef.current = setInterval(() => setSeconds((s) => {
        if (s + 1 >= MAX_SECONDS) { stopRecording(); }
        return s + 1;
      }), 1000);
    } catch (e) {
      setError("Microphone permission was denied or unavailable. You can type the contact below instead.");
      setPhase("error");
    }
  };

  const stopRecording = async () => {
    if (timerRef.current) { clearInterval(timerRef.current); timerRef.current = null; }
    try { if (recognitionRef.current) { recognitionRef.current.onend = null; recognitionRef.current.stop(); recognitionRef.current = null; } } catch {}
    const mr = mediaRef.current;
    if (!mr || mr.state === "inactive") return;
    await new Promise((res) => { mr.onstop = ((orig) => () => { orig && orig(); res(); })(mr.onstop); mr.stop(); });
    if (streamRef.current) { streamRef.current.getTracks().forEach((t) => t.stop()); streamRef.current = null; }
    await processAudio();
  };

  const cancelRecording = () => {
    stopEverything();
    chunksRef.current = [];
    blobRef.current = null;
    setSeconds(0);
    setPhase("idle");
  };

  const processAudio = async () => {
    setPhase("processing");
    setError("");
    try {
      const blob = blobRef.current;
      if (!blob || blob.size < 1000) throw { response: { data: { detail: "The recording was empty or silent. Please record again." } } };
      const ext = (blob.type.includes("mp4") ? "mp4" : blob.type.includes("ogg") ? "ogg" : "webm");
      const fd = new FormData();
      fd.append("file", blob, `voice.${ext}`);
      const t = await api.post(`/voice/transcribe?workspace_id=${workspaceId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const txt = (t.data.transcript || "").trim();
      setTranscript(txt);
      if (!txt) { setError("No speech detected. You can type the contact, or record again."); setPhase("error"); return; }
      await runExtraction(txt);
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Transcription failed. You can type the contact, or retry.");
      setPhase("error");
    }
  };

  const runExtraction = async (txt) => {
    setPhase("processing");
    try {
      const { data } = await api.post(`/voice/draft?workspace_id=${workspaceId}`, { transcript: txt, origin_id: originId || null, source_entity_id: sourceEntity?.id || null });
      const built = (data.drafts || []).map((d) => cardToDraft(d, originId, sourceEntity));
      setDrafts(built.length ? built : [cardToDraft({ card: {} }, originId, sourceEntity)]);
      setPhase("review");
    } catch (err) {
      setError(apiError(err.response?.data?.detail) || "Could not extract fields. Edit the transcript and retry, or type the contact.");
      setPhase("review"); // keep transcript editable
      setDrafts([cardToDraft({ card: {} }, originId, sourceEntity)]);
    }
  };

  const startTyped = () => {
    setTranscript("");
    setDrafts([cardToDraft({ card: {} }, originId, sourceEntity)]);
    setPhase("review");
  };

  const toggleReplay = () => {
    const el = audioElRef.current;
    if (!el) return;
    if (playing) { el.pause(); setPlaying(false); } else { el.play(); setPlaying(true); }
  };

  const setD = (i, patch) => setDrafts((ds) => ds.map((d, idx) => (idx === i ? { ...d, ...patch } : d)));

  const save = async () => {
    setSaving(true);
    try {
      const entities = [], memberships = [], relationships = [], tasks = [], unresolved = [];
      if (sourceEntity) entities.push({ ref: "ctx", action: "link", entity_id: sourceEntity.id });
      // Resolve canonical "Me" once if any context connection targets it (explicit setup required).
      if (drafts.some((d) => d.context_connect && d.context_target === "me")) {
        const { data: me } = await api.get(`/me/contact?workspace_id=${workspaceId}`);
        if (me.needs_setup) { toast.error("Set up “Me” first: open any contact → Add connection → Me. Then link to yourself by voice."); setSaving(false); return; }
        entities.push({ ref: "me", action: "link", entity_id: me.contact_id });
      }
      drafts.forEach((d) => {
        const phones = d.phones.split(",").map((x) => x.trim()).filter(Boolean);
        const emails = d.emails.split(",").map((x) => x.trim()).filter(Boolean);
        const pPhones = d.phone_ambiguous ? (d.phone_owner === "person" ? phones : []) : phones;
        const bPhones = d.phone_ambiguous ? (d.phone_owner === "business" ? phones : []) : [];
        const hasPerson = !!d.person_name.trim();
        const hasBusiness = !!d.business.trim();
        if (hasPerson) {
          entities.push(d.person_action === "link"
            ? { ref: d.pRef, action: "link", entity_id: d.person_link_id, phones: pPhones, emails }
            : { ref: d.pRef, action: "create", name: d.person_name, type: d.person_type, role: d.role, phones: pPhones, emails, address: d.address, website: hasBusiness ? "" : d.website, notes: d.notes });
          if (d.add_membership && d.membership_origin) memberships.push({ entity_ref: d.pRef, origin_id: d.membership_origin });
          if (d.context_connect) relationships.push({ source_ref: d.context_target === "me" ? "me" : "ctx", target_ref: d.pRef, type: d.context_rel_type, direction: "directed", status: "reported", source: "Voice entry" });
          if (d.make_task && d.task_title.trim()) tasks.push({ title: d.task_title, entity_refs: [d.pRef], due_date: d.reminder_date || null, reminder_date: d.reminder_date || null });
        }
        if (hasBusiness) {
          entities.push(d.business_action === "link"
            ? { ref: d.bRef, action: "link", entity_id: d.business_link_id, phones: bPhones }
            : { ref: d.bRef, action: "create", name: d.business, type: d.business_type, phones: bPhones, address: hasPerson ? "" : d.address, website: d.website });
          if (d.add_membership && d.membership_origin && !hasPerson) memberships.push({ entity_ref: d.bRef, origin_id: d.membership_origin });
          if (hasPerson) relationships.push({ source_ref: d.pRef, target_ref: d.bRef, type: d.relationship_type || "works_for", custom_label: "", direction: "directed", status: "reported", source: "Voice entry" });
          if (!hasPerson && d.make_task && d.task_title.trim()) tasks.push({ title: d.task_title, entity_refs: [d.bRef], due_date: d.reminder_date || null, reminder_date: d.reminder_date || null });
        }
        // Smart relationship phrases: link/create each mentioned connection
        const subjectRef = hasPerson ? d.pRef : (hasBusiness ? d.bRef : null);
        if (subjectRef) {
          (d.related || []).forEach((rc) => {
            if (!rc.include || !rc.name.trim()) return;
            // Require an explicit resolution when candidates exist.
            if (rc.dups && rc.dups.length > 0 && !(rc.action === "create" || (rc.action === "link" && rc.link_id))) {
              unresolved.push(rc.name.trim());
              return;
            }
            if (rc.action === "link" && rc.link_id) {
              entities.push({ ref: rc.relRef, action: "link", entity_id: rc.link_id });
            } else {
              entities.push({ ref: rc.relRef, action: "create", name: rc.name.trim(), type: "person" });
            }
            relationships.push({ source_ref: subjectRef, target_ref: rc.relRef, type: rc.relationship_type, custom_label: "", direction: "directed", status: "reported", source: "Voice entry" });
          });
        }
      });
      if (unresolved.length > 0) {
        toast.error(`Choose how to handle the connection${unresolved.length > 1 ? "s" : ""}: ${unresolved.join(", ")} — link to an existing contact or create new.`);
        setSaving(false);
        return;
      }
      if (entities.filter((e) => e.ref !== "ctx").length === 0) { toast.error("Add at least a person or a business name"); setSaving(false); return; }
      const { data } = await api.post(`/voice/commit?workspace_id=${workspaceId}`, { idempotency_key: idemRef.current, transcript, entities, memberships, relationships, tasks });
      setSavedResult(data);
      setPhase("saved");
      onSaved && onSaved();
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || "Save failed — your draft is preserved. Retry.");
    } finally { setSaving(false); }
  };

  const mm = String(Math.floor(seconds / 60)).padStart(1, "0");
  const ss = String(seconds % 60).padStart(2, "0");

  return (
    <Modal open onClose={() => { stopEverything(); onClose(); }} title="Add contact by voice" width={640} testid="voice-modal">
      {status && !status.configured && (
        <div className="mb-4 p-3 text-xs" style={{ border: "1px solid var(--si-warning)", borderRadius: 2, color: "var(--si-warning)" }}>
          Voice transcription is not configured on the server (EMERGENT_LLM_KEY). You can still type the contact below.
        </div>
      )}

      {phase === "idle" && (
        <div className="text-center py-6">
          <button data-testid="voice-record-btn" onClick={startRecording} disabled={status && !status.configured}
            className="mx-auto flex items-center justify-center transition-transform duration-200 hover:scale-105 disabled:opacity-40"
            style={{ width: 96, height: 96, borderRadius: "50%", background: "var(--si-teal)", border: "2px solid var(--si-teal-light)" }}>
            <Mic size={38} color="#fff" strokeWidth={1.5} />
          </button>
          <div className="mt-5 text-sm" style={{ color: "var(--si-text)" }}>Tap to record, then speak the contact naturally.</div>
          <div className="mt-2 text-xs max-w-md mx-auto" style={{ color: "var(--si-text-2)" }}>
            e.g. "John Smith, owns Smith Plumbing, 631-555-0123, introduced by Margaret. Add to my office Spider. Then Aisha Khan, runs Khan Design, call her Friday." — list several contacts in one go.
          </div>
          {status && <div className="mt-3 text-xs" style={{ color: "var(--si-text-2)" }}>{status.note} Max {status.max_seconds}s.</div>}
          <div className="mt-5">
            <Btn variant="ghost" onClick={startTyped} data-testid="voice-type-instead"><Keyboard size={14} /> Type instead</Btn>
          </div>
        </div>
      )}

      {phase === "recording" && (
        <div className="text-center py-8">
          <div className="mx-auto flex items-center justify-center blink" style={{ width: 96, height: 96, borderRadius: "50%", background: "rgba(239,68,68,0.15)", border: "2px solid var(--si-danger)" }}>
            <Mic size={38} color="#EF4444" strokeWidth={1.5} />
          </div>
          <div className="mt-5 font-mono text-2xl" style={{ color: "var(--si-text)" }}>{mm}:{ss}</div>
          <div className="si-label mt-1">recording… (auto-stops at {MAX_SECONDS}s)</div>
          <div className="mt-4 mx-auto max-w-md text-left p-3" style={{ background: "var(--si-bg)", border: "1px solid var(--si-border)", borderRadius: 2, minHeight: 64 }} data-testid="live-caption-box">
            <div className="si-label mb-1">{liveSupported ? "Live caption (draft — final transcript is produced after you stop)" : "Live captions unavailable in this browser — the final transcript is produced after you stop."}</div>
            {liveSupported && (
              <div className="text-sm" style={{ color: "var(--si-text)" }}>
                <span>{liveFinal}</span>{" "}
                <span style={{ color: "var(--si-text-2)", fontStyle: "italic" }} data-testid="live-caption-interim">{liveInterim}</span>
                {!liveFinal && !liveInterim && <span className="si-label">Listening…</span>}
              </div>
            )}
          </div>
          <div className="mt-6 flex gap-3 justify-center">
            <Btn variant="primary" onClick={stopRecording} data-testid="voice-stop-btn"><Square size={15} /> Stop & transcribe</Btn>
            <Btn variant="danger" onClick={cancelRecording} data-testid="voice-cancel-btn"><X size={15} /> Cancel</Btn>
          </div>
        </div>
      )}

      {phase === "processing" && (
        <div className="text-center py-16">
          <Loader2 size={30} className="mx-auto animate-spin" style={{ color: "var(--si-teal-light)" }} />
          <div className="mt-4 text-sm font-mono" style={{ color: "var(--si-silver)" }}>Transcribing & extracting…</div>
        </div>
      )}

      {phase === "error" && (
        <div className="py-6">
          <div className="flex items-start gap-2 p-3 mb-4" style={{ border: "1px solid var(--si-danger)", borderRadius: 2 }}>
            <AlertTriangle size={16} style={{ color: "var(--si-danger)" }} />
            <div className="text-sm" style={{ color: "var(--si-text)" }}>{error}</div>
          </div>
          <div className="flex gap-2 justify-center">
            <Btn variant="secondary" onClick={() => setPhase("idle")} data-testid="voice-retry-btn"><RotateCcw size={14} /> Try recording again</Btn>
            <Btn variant="primary" onClick={startTyped}><Keyboard size={14} /> Type the contact</Btn>
          </div>
        </div>
      )}

      {phase === "review" && (
        <div className="space-y-5 max-h-[65vh] overflow-y-auto pr-1">
          <div>
            <div className="flex items-center justify-between mb-1">
              <div className="si-label">Transcript (editable)</div>
              {audioUrl && (
                <button onClick={toggleReplay} className="text-xs flex items-center gap-1" style={{ color: "var(--si-silver)" }} data-testid="voice-replay">
                  {playing ? <Pause size={12} /> : <Play size={12} />} replay audio
                </button>
              )}
            </div>
            <Textarea value={transcript} onChange={(e) => setTranscript(e.target.value)} data-testid="voice-transcript" placeholder="Type or dictate the contact details…" />
            {audioUrl && <audio ref={audioElRef} src={audioUrl} onEnded={() => setPlaying(false)} className="hidden" />}
            {transcript && <div className="mt-2"><Btn variant="ghost" className="!py-1 !px-2 text-xs" onClick={() => runExtraction(transcript)} data-testid="voice-reextract"><RotateCcw size={12} /> Re-extract from transcript</Btn></div>}
          </div>

          {drafts.length > 1 && <div className="si-label" style={{ color: "var(--si-silver)" }}>{drafts.length} separate contacts detected — review each.</div>}

          {drafts.map((d, i) => (
            <div key={i} data-testid={`voice-card-${i}`} className="p-4 space-y-3" style={{ border: "1px solid var(--si-border)", borderRadius: 2, background: "var(--si-surface)" }}>
              <div className="si-label flex items-center justify-between gap-2">
                <span className="flex items-center gap-2">Contact {i + 1}{d.uncertain_fields.length > 0 && <Badge value="uncertain">uncertain: {d.uncertain_fields.join(", ")}</Badge>}</span>
                {drafts.length > 1 && <button onClick={() => setDrafts((ds) => ds.filter((_, idx) => idx !== i))} className="text-xs flex items-center gap-1" style={{ color: "var(--si-danger)" }} data-testid={`voice-card-remove-${i}`}><X size={12} /> remove</button>}
              </div>

              {/* Person */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Person name"><Input value={d.person_name} onChange={(e) => setD(i, { person_name: e.target.value })} data-testid={`voice-person-${i}`} /></Field>
                <Field label="Role / title"><Input value={d.role} onChange={(e) => setD(i, { role: e.target.value })} /></Field>
              </div>
              {d.person_name && d.person_dups.length > 0 && (
                <DupPicker label="Possible existing person" dups={d.person_dups} action={d.person_action} linkId={d.person_link_id}
                  onAction={(a) => setD(i, { person_action: a })} onLink={(id) => setD(i, { person_link_id: id, person_action: "link" })} />
              )}

              {/* Business */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Business / organization"><Input value={d.business} onChange={(e) => setD(i, { business: e.target.value })} data-testid={`voice-business-${i}`} /></Field>
                {d.person_name && d.business && <Field label="Person → business"><Select value={d.relationship_type} onChange={(e) => setD(i, { relationship_type: e.target.value })}>{REL_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</Select></Field>}
              </div>
              {d.business && d.business_dups.length > 0 && (
                <DupPicker label="Possible existing business" dups={d.business_dups} action={d.business_action} linkId={d.business_link_id}
                  onAction={(a) => setD(i, { business_action: a })} onLink={(id) => setD(i, { business_link_id: id, business_action: "link" })} />
              )}

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Phone(s)"><Input value={d.phones} onChange={(e) => setD(i, { phones: e.target.value })} /></Field>
                <Field label="Email(s)"><Input value={d.emails} onChange={(e) => setD(i, { emails: e.target.value })} /></Field>
              </div>
              {d.phone_ambiguous && d.person_name && d.business && (
                <div className="p-2 text-xs" style={{ border: "1px solid var(--si-warning)", borderRadius: 2 }}>
                  <div className="mb-1" style={{ color: "var(--si-warning)" }}>Who owns the phone number?</div>
                  <label className="mr-4" style={{ color: "var(--si-text)" }}><input type="radio" checked={d.phone_owner === "person"} onChange={() => setD(i, { phone_owner: "person" })} data-testid={`voice-phone-person-${i}`} /> {d.person_name}</label>
                  <label style={{ color: "var(--si-text)" }}><input type="radio" checked={d.phone_owner === "business"} onChange={() => setD(i, { phone_owner: "business" })} /> {d.business}</label>
                </div>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Field label="Address"><Input value={d.address} onChange={(e) => setD(i, { address: e.target.value })} /></Field>
                <Field label="Website"><Input value={d.website} onChange={(e) => setD(i, { website: e.target.value })} /></Field>
              </div>
              <Field label="Notes"><Textarea value={d.notes} onChange={(e) => setD(i, { notes: e.target.value })} /></Field>

              {/* Membership */}
              <div className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-2 text-sm" style={{ color: "var(--si-text)" }}>
                  <input type="checkbox" checked={d.add_membership} onChange={(e) => setD(i, { add_membership: e.target.checked })} data-testid={`voice-membership-${i}`} /> Add to Spider origin
                </label>
                {d.add_membership && <Select value={d.membership_origin} onChange={(e) => setD(i, { membership_origin: e.target.value })} style={{ width: "auto" }}><option value="">— select origin —</option>{origins.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}</Select>}
                {d.origin_auto && <Badge value="confirmed">voice: “{d.origin_auto}”</Badge>}
              </div>

              {/* Context connection */}
              {sourceEntity && (
                <div className="flex items-center gap-2 flex-wrap p-2" style={{ background: "var(--si-bg)", borderRadius: 2 }}>
                  <label className="flex items-center gap-2 text-sm" style={{ color: "var(--si-text)" }}>
                    <input type="checkbox" checked={d.context_connect} onChange={(e) => setD(i, { context_connect: e.target.checked })} data-testid={`voice-context-${i}`} /> Connect to
                  </label>
                  {d.context_connect && (
                    <>
                      <Select value={d.context_target} onChange={(e) => setD(i, { context_target: e.target.value })} style={{ width: "auto" }} data-testid={`voice-context-target-${i}`}>
                        <option value="source">{sourceEntity.name}</option>
                        <option value="me">Me</option>
                      </Select>
                      <span className="si-label">as</span>
                      <Select value={d.context_rel_type} onChange={(e) => setD(i, { context_rel_type: e.target.value })} style={{ width: "auto" }}>{REL_TYPES.map((t) => <option key={t} value={t}>{t.replace(/_/g, " ")}</option>)}</Select>
                    </>
                  )}
                </div>
              )}

              {/* Task + reminder */}
              <div className="flex items-center gap-2 flex-wrap">
                <label className="flex items-center gap-2 text-sm" style={{ color: "var(--si-text)" }}>
                  <input type="checkbox" checked={d.make_task} onChange={(e) => setD(i, { make_task: e.target.checked })} data-testid={`voice-task-${i}`} /> Create follow-up task
                </label>
                {d.make_task && <Input value={d.task_title} onChange={(e) => setD(i, { task_title: e.target.value })} placeholder="Task title" style={{ flex: 1, minWidth: 140 }} />}
                {d.make_task && <Input type="date" value={d.reminder_date} onChange={(e) => setD(i, { reminder_date: e.target.value })} style={{ width: "auto" }} data-testid={`voice-reminder-${i}`} />}
                {d.reminder_auto && d.reminder_date && <Badge value="confirmed">voice reminder</Badge>}
              </div>

              {/* Smart relationship phrases */}
              {d.related && d.related.length > 0 && (
                <div className="p-2 space-y-2" style={{ border: "1px dashed var(--si-teal)", borderRadius: 2 }} data-testid={`voice-related-${i}`}>
                  <div className="si-label" style={{ color: "var(--si-silver)" }}>Connections mentioned</div>
                  {d.related.map((rc, ri) => {
                    const setR = (patch) => setD(i, { related: d.related.map((x, xi) => (xi === ri ? { ...x, ...patch } : x)) });
                    return (
                      <div key={ri} className="text-xs p-2" style={{ background: "var(--si-bg)", borderRadius: 2 }} data-testid={`voice-related-${i}-${ri}`}>
                        <label className="flex items-center gap-2 mb-1.5" style={{ color: "var(--si-text)" }}>
                          <input type="checkbox" checked={rc.include} onChange={(e) => setR({ include: e.target.checked })} data-testid={`voice-related-include-${i}-${ri}`} />
                          <span className="italic" style={{ color: "var(--si-text-2)" }}>{rc.phrase || `${rc.relationship_type.replace(/_/g, " ")} ${rc.name}`}</span>
                        </label>
                        {rc.include && (
                          <div className="grid grid-cols-2 gap-2">
                            <div>
                              <div className="si-label mb-1">Connection</div>
                              <Input value={rc.name} onChange={(e) => setR({ name: e.target.value })} data-testid={`voice-related-name-${i}-${ri}`} />
                            </div>
                            <div>
                              <div className="si-label mb-1">Relationship</div>
                              <Select value={rc.relationship_type} onChange={(e) => setR({ relationship_type: e.target.value })} data-testid={`voice-related-type-${i}-${ri}`}>{REL_TYPES.map((t) => <option key={t} value={t}>{`${d.person_name || "this contact"} ${t.replace(/_/g, " ")}…`}</option>)}</Select>
                            </div>
                            <div className="col-span-2">
                              {rc.dups && rc.dups.length > 0 ? (
                                <div className="p-1.5" style={{ border: `1px solid ${rc.action === "" ? "var(--si-warning)" : "var(--si-teal)"}`, borderRadius: 2 }}>
                                  {rc.action === "" && <div className="si-label mb-1" style={{ color: "var(--si-warning)" }}>Choose one — {rc.dups.length} possible match{rc.dups.length > 1 ? "es" : ""}</div>}
                                  <label className="flex items-center gap-2 mb-0.5" style={{ color: "var(--si-text)" }}>
                                    <input type="radio" checked={rc.action === "create"} onChange={() => setR({ action: "create", link_id: "" })} data-testid={`voice-related-create-${i}-${ri}`} /> Create as new contact
                                  </label>
                                  {rc.dups.map((dp) => (
                                    <label key={dp.id} className="flex items-center gap-2 mb-0.5" style={{ color: "var(--si-text)" }}>
                                      <input type="radio" checked={rc.action === "link" && rc.link_id === dp.id} onChange={() => setR({ action: "link", link_id: dp.id })} data-testid={`voice-related-link-${i}-${ri}`} /> Link to existing: {dp.name} <span className="si-label">{dp.type}</span>
                                    </label>
                                  ))}
                                </div>
                              ) : (
                                <span className="si-label" style={{ color: "var(--si-text-2)" }}>No existing match — will be created as a new contact.</span>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}

          <div className="text-xs p-2" style={{ color: "var(--si-text-2)" }}>
            Nothing is saved until you press Save. Information extracted from speech is marked uncertain and never independently verified.
          </div>
          <div className="flex justify-between">
            <Btn variant="ghost" onClick={() => { stopEverything(); onClose(); }} data-testid="voice-discard">Discard draft</Btn>
            <Btn variant="primary" onClick={save} disabled={saving} data-testid="voice-save"><Check size={15} /> {saving ? "Saving…" : "Save contact"}</Btn>
          </div>
        </div>
      )}

      {phase === "saved" && savedResult && (
        <div className="py-6 text-center">
          <div className="mx-auto flex items-center justify-center mb-4" style={{ width: 56, height: 56, borderRadius: "50%", background: "rgba(16,185,129,0.15)", border: "1px solid var(--si-success)" }}>
            <Check size={26} style={{ color: "var(--si-success)" }} />
          </div>
          <div className="text-sm" style={{ color: "var(--si-text)" }}>Saved {savedResult.created_count} record(s).</div>
          <div className="text-xs mt-1" style={{ color: "var(--si-text-2)" }}>{savedResult.entities.map((e) => `${e.name} (${e.action})`).join(", ")}</div>
          <div className="mt-6 flex gap-2 justify-center flex-wrap">
            {savedResult.primary_entity_id && <Btn variant="secondary" onClick={() => { onClose(); navigate(`/app/entities/${savedResult.primary_entity_id}`); }} data-testid="voice-open-file"><ExternalLink size={14} /> Open file</Btn>}
            {savedResult.primary_entity_id && <Btn variant="ghost" onClick={() => { onClose(); navigate(`/app/entities/${savedResult.primary_entity_id}`); }}><Share2 size={14} /> View network</Btn>}
            <Btn variant="primary" onClick={() => { idemRef.current = newRef() + newRef(); setSavedResult(null); setTranscript(""); setDrafts([]); setAudioUrl(null); setPhase("idle"); }} data-testid="voice-add-another"><Plus size={14} /> Add another</Btn>
          </div>
        </div>
      )}
    </Modal>
  );
}

function DupPicker({ label, dups, action, linkId, onAction, onLink }) {
  return (
    <div className="p-2 text-xs" style={{ border: "1px solid var(--si-teal)", borderRadius: 2 }}>
      <div className="si-label mb-1" style={{ color: "var(--si-silver)" }}>{label} — do not merge on name alone</div>
      <label className="flex items-center gap-2 mb-1" style={{ color: "var(--si-text)" }}>
        <input type="radio" checked={action === "create"} onChange={() => onAction("create")} /> Create as new contact
      </label>
      {dups.map((dp) => (
        <label key={dp.id} className="flex items-center gap-2 mb-0.5" style={{ color: "var(--si-text)" }}>
          <input type="radio" checked={action === "link" && linkId === dp.id} onChange={() => onLink(dp.id)} /> Link to existing: {dp.name} <span className="si-label">{dp.type}</span>
        </label>
      ))}
    </div>
  );
}
